import { useMemo, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react'
import AppLayout from '@/components/AppLayout'
import PageTitle from '@/components/PageTitle'
import {
  applyTableState,
  type Column,
  DataTable,
  DataTableToolbar,
  useTableState,
} from '@/components/blocks/data-table'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/DropdownMenu'
import { EmptyState } from '@/components/ui/EmptyState'
import { ConfirmDialog, type ConfirmRequest } from '@/components/shared'
import RoleFormDialog, { type Role } from '@/components/roles/RoleFormDialog'

interface RolesIndexProps {
  roles: Role[]
  available_permissions: string[]
}

const ROLE_COLUMNS: Column<Role>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 'minmax(140px,1fr)',
    sortable: true,
    cell: (row) => <span className="text-fg font-semibold">{row.name}</span>,
  },
  {
    key: 'key',
    header: 'Key',
    width: '140px',
    sortable: true,
    cell: (row) => (
      <Badge variant="outline" translate="no">
        {row.key}
      </Badge>
    ),
  },
  {
    key: 'description',
    header: 'Description',
    width: 'minmax(200px,1.6fr)',
    cell: (row) =>
      row.description ? (
        <span className="text-fg-muted">{row.description}</span>
      ) : (
        <span className="text-fg-subtle">No description</span>
      ),
  },
  {
    key: 'permissions',
    header: 'Permissions',
    width: 'minmax(180px,1.4fr)',
    cell: (row) =>
      row.permissions.length === 0 ? (
        <span className="text-fg-subtle">—</span>
      ) : (
        <span className="flex flex-wrap gap-1">
          {row.permissions.map((perm) => (
            <Badge key={perm} size="tag" variant="neutral">
              {perm}
            </Badge>
          ))}
        </span>
      ),
  },
]

export default function RolesIndex({ roles, available_permissions }: RolesIndexProps) {
  const { state, setState } = useTableState({ pageSize: 25, sortKey: 'name', sortDir: 'asc' })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Role | null>(null)
  const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null)

  const table = useMemo(() => applyTableState(roles, ROLE_COLUMNS, state), [roles, state])

  const openNew = () => {
    setEditing(null)
    setDialogOpen(true)
  }

  const openEdit = (role: Role) => {
    setEditing(role)
    setDialogOpen(true)
  }

  const requestDelete = (role: Role) =>
    setConfirmRequest({
      title: `Delete ${role.name}?`,
      description: 'Anyone holding this role loses its permissions immediately.',
      confirmLabel: 'Delete Role',
      destructive: true,
      onConfirm: () => router.delete(`/roles/${role.id}`, { preserveScroll: true }),
    })

  return (
    <AppLayout
      header={<PageTitle title="Roles" subtitle={`${roles.length} roles`} />}
      headerActions={
        <Button onClick={openNew}>
          <Plus />
          New Role
        </Button>
      }
    >
      <Head title="Roles" />

      <div className="space-y-3">
        <DataTableToolbar
          state={state}
          onStateChange={setState}
          searchPlaceholder="Search name, key or description…"
        />

        <DataTable
          columns={ROLE_COLUMNS}
          rows={table.rows}
          total={table.total}
          state={state}
          onStateChange={setState}
          rowKey={(row) => row.id}
          minWidth={900}
          empty={
            <EmptyState
              title={roles.length === 0 ? 'No roles yet.' : 'No roles match your search.'}
              action={
                roles.length === 0 ? undefined : (
                  <Button variant="outline" onClick={() => setState({ search: '', filters: {} })}>
                    Clear Filters
                  </Button>
                )
              }
            />
          }
          rowActions={(row) => (
            <DropdownMenu
              trigger={
                <Button variant="ghost" size="icon" aria-label={`Actions for ${row.name}`}>
                  <MoreHorizontal />
                </Button>
              }
            >
              {/* The admin role is structural — it cannot be edited away. */}
              <DropdownMenuItem disabled={row.key === 'admin'} onClick={() => openEdit(row)}>
                <Pencil />
                Edit Role
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                destructive
                disabled={row.key === 'admin'}
                onClick={() => requestDelete(row)}
              >
                <Trash2 />
                Delete Role
              </DropdownMenuItem>
            </DropdownMenu>
          )}
        />
      </div>

      {dialogOpen && (
        <RoleFormDialog
          key={editing?.id ?? 'new'}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          role={editing}
          availablePermissions={available_permissions}
        />
      )}

      <ConfirmDialog request={confirmRequest} onClose={() => setConfirmRequest(null)} />
    </AppLayout>
  )
}
